import React, { useEffect, useRef, useState } from "react";
import { Avatar } from "@/types/interfaces";
import { convertToInches } from "@/Utils";
import TextInputModal from "../TextInputModal";
import {
  FeetInchesField,
  NumberField,
  seedDecimal,
} from "../TextInputModal/fields";

interface HeightModalProps {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  newAvatar: Avatar;
  handlePickerChange: (key: string, value: object) => void;
  unit: string;
  setUnit: React.Dispatch<React.SetStateAction<"inch" | "cm">>;
}

// Mirror the wheel picker's ranges so the existing closeHeightModal logic and
// convertCmToFeetInches/convertToInches keep working unchanged.
const FEET_MIN = 3;
const FEET_MAX = 8;
const INCH_MIN = 0;
// Exclusive: the inches box takes one decimal, so 11.9" is a height and 12" is
// not one — it is 1'0". Range checks compare against this, never `<= 11`.
const INCH_LIMIT = 12;
const CM_MIN = 91;
const CM_MAX = 273;

const MIN_TOTAL_INCHES = FEET_MIN * 12; // 3'0"
const MAX_TOTAL_INCHES = FEET_MAX * 12 + 11.9; // 8'11.9"

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max);

/** The one decimal place every field in this modal can display. */
const round1 = (n: number) => Math.round(n * 10) / 10;

const cmToTotalInches = (cm: number) => cm / 2.54;

/** The two halves of one conversion — the same height in both units. */
type HeightPair = { cm: string; feet: string; inches: string };

/** Total inches → the number the cm field holds, clamped to the cm range. */
const totalInchesToCm = (totalInches: number) =>
  String(clamp(round1(totalInches * 2.54), CM_MIN, CM_MAX));

/**
 * Total inches → the two strings the feet/inches fields hold.
 *
 * Rounding the remainder can still land on 12" (11.97" rounds to 12.0), which
 * is not a value these fields can show — carry it into feet. Clamping first
 * keeps a converted value inside the range `validate()` accepts, so switching
 * units never produces an error on a number the user never typed.
 */
const totalInchesToFeetInches = (totalInches: number) => {
  const total = clamp(totalInches, MIN_TOTAL_INCHES, MAX_TOTAL_INCHES);
  let feet = Math.floor(total / 12);
  let inches = round1(total % 12);
  if (inches >= INCH_LIMIT) {
    feet += 1;
    inches = 0;
  }
  return { feet: String(feet), inches: String(inches) };
};

/**
 * The cm draft as one number string.
 *
 * New drafts carry the whole value — fraction included — on `heightCmTens` and
 * park `heightCmOnes` at "0". Drafts written by the pre-2.4.19 wheel split it,
 * with the half step on `heightCmOnes`; summing covers both, and seeding
 * rewrites the pair into the current shape (see `writeCmDraft`).
 */
const draftCm = (avatar: Avatar): string => {
  const tens = seedDecimal(avatar.heightCmTens);
  if (tens === "") return "";
  const ones = parseFloat(avatar.heightCmOnes ?? "");
  return String(round1(parseFloat(tens) + (isNaN(ones) ? 0 : ones)));
};

/**
 * The height the form row is currently showing, in total inches — or null when
 * the user has not set one yet.
 *
 * `newAvatar.height` is the committed field and is always a feet-inches string
 * (`5'10"`, or `5'10.1''` when closeHeightModal converted a cm entry). The two
 * draft-slot fallbacks cover a draft that has not been committed yet.
 */
const committedTotalInches = (avatar: Avatar): number | null => {
  if (avatar.height) {
    const total = convertToInches(avatar.height);
    if (!isNaN(total)) return total;
  }
  const cm = draftCm(avatar);
  if (cm !== "") return cmToTotalInches(parseFloat(cm));
  const feet = seedDecimal(avatar.heightFt);
  if (feet !== "") {
    const inches = seedDecimal(avatar.heightInch);
    return parseFloat(feet) * 12 + (inches === "" ? 0 : parseFloat(inches));
  }
  return null;
};

/**
 * What the fields show when the modal opens: the draft for the active unit if
 * there is one, otherwise the committed height converted into that unit.
 *
 * That second branch is what makes a height entered in cm come back as
 * feet/inches (and vice versa). closeHeightModal clears the *other* unit's
 * draft slots on commit, so on a remount — the form is rehydrated from
 * localStorage every time the user returns from the scan — the active unit's
 * slot is empty even though the user did enter a height.
 */
const seedCm = (avatar: Avatar): { value: string; pair: HeightPair | null } => {
  const own = draftCm(avatar);
  if (own !== "") return { value: own, pair: null };
  const total = committedTotalInches(avatar);
  if (total === null) return { value: "", pair: null };
  const value = totalInchesToCm(total);
  return { value, pair: { cm: value, ...totalInchesToFeetInches(total) } };
};

const seedFeetInches = (
  avatar: Avatar,
): { value: { feet: string; inches: string }; pair: HeightPair | null } => {
  const feet = seedDecimal(avatar.heightFt);
  if (feet !== "") {
    const inches = seedDecimal(avatar.heightInch);
    return {
      value: { feet, inches: inches === "" ? "0" : inches },
      pair: null,
    };
  }
  const total = committedTotalInches(avatar);
  if (total === null)
    return { value: { feet: "", inches: "" }, pair: null };
  const value = totalInchesToFeetInches(total);
  return { value, pair: { cm: totalInchesToCm(total), ...value } };
};

const HeightModal: React.FC<HeightModalProps> = ({
  open,
  onClose,
  onCancel,
  newAvatar,
  handlePickerChange,
  unit,
  setUnit,
}) => {
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");
  const [cm, setCm] = useState("");

  // Both halves of the last conversion. cm is finer-grained than whole inches
  // (1" = 2.54cm), so several cm values round to the same feet/inches and
  // converting back can only pick one: 92cm → 3'0" → *91*cm. 61% of the cm range
  // fails that round trip. Rather than recompute, hand back what the user typed
  // while the converted field is still untouched — the equality check below is
  // what detects "untouched". Same fix as WeightModal; see the note there.
  const lastPair = useRef<HeightPair | null>(null);

  // Writing the draft slots is how the modal publishes a value — closeHeightModal
  // reads them, never local state. Keep every path that changes what is on
  // screen (seed, unit switch, typing) paired with the matching write, or Ok
  // commits something the user never saw. See HERE_BE_DRAGONS.md § Avatar form
  // modals.
  const writeInchDraft = (f: string, i: string) => {
    handlePickerChange("heightFt", { heightFt: f ? `${f}'` : "" });
    handlePickerChange("heightInch", { heightInch: i !== "" ? `${i}"` : "" });
  };
  // `heightCmTens` carries the whole cm value, fraction included; `heightCmOnes`
  // is parked at "0" because a pre-2.4.19 draft may hold the half step there and
  // `draftCm`/`closeHeightModal` both add the two slots together.
  const writeCmDraft = (c: string) => {
    handlePickerChange("heightCmTens", { heightCmTens: c });
    handlePickerChange("heightCmOnes", { heightCmOnes: c ? "0" : "" });
  };

  useEffect(() => {
    if (!open) return;
    if (unit === "cm") {
      const { value, pair } = seedCm(newAvatar);
      setCm(value);
      writeCmDraft(value);
      lastPair.current = pair;
    } else {
      const { value, pair } = seedFeetInches(newAvatar);
      setFeet(value.feet);
      setInches(value.inches);
      writeInchDraft(value.feet, value.inches);
      lastPair.current = pair;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Switching units keeps the number the user is looking at: convert what is on
  // screen rather than re-reading the draft, because the other unit's slot holds
  // a stale value (or nothing at all). Deliberately does NOT clear the slot we
  // are switching away from — the Height row on the form renders `heightCmTens`
  // whenever convertHeight is set, so clearing it would blank the row if the
  // user then cancels.
  const handleUnitChange = (next: string) => {
    if (next === unit) return;
    const pair = lastPair.current;

    if (next === "cm") {
      const untouched =
        pair && pair.feet === feet && pair.inches === inches ? pair.cm : null;
      let converted: string;
      if (untouched !== null) {
        converted = untouched;
      } else {
        const f = parseFloat(feet);
        const i = parseFloat(inches);
        converted = isNaN(f)
          ? ""
          : totalInchesToCm(f * 12 + (isNaN(i) ? 0 : i));
        lastPair.current = isNaN(f) ? null : { cm: converted, feet, inches };
      }
      setCm(converted);
      writeCmDraft(converted);
    } else {
      const untouched = pair && pair.cm === cm ? pair : null;
      let converted: { feet: string; inches: string };
      if (untouched !== null) {
        converted = { feet: untouched.feet, inches: untouched.inches };
      } else {
        const c = parseFloat(cm);
        converted = isNaN(c)
          ? { feet: "", inches: "" }
          : totalInchesToFeetInches(cmToTotalInches(c));
        lastPair.current = isNaN(c) ? null : { cm, ...converted };
      }
      setFeet(converted.feet);
      setInches(converted.inches);
      writeInchDraft(converted.feet, converted.inches);
    }

    setUnit(next as "inch" | "cm");
  };

  // The isNaN guards are what reject a lone "." — the fields keep a separator
  // the user has typed but not yet completed, and a bare "." is truthy while
  // every `<`/`>` comparison against NaN is false, so without them it submits.
  const validate = () => {
    if (unit === "cm") {
      const n = parseFloat(cm);
      if (!cm || isNaN(n) || n < CM_MIN || n > CM_MAX)
        return "Invalid height input";
      return null;
    }
    const f = parseFloat(feet);
    const i = parseFloat(inches);
    if (!feet || isNaN(f) || f < FEET_MIN || f > FEET_MAX)
      return "Invalid height input";
    if (inches === "" || isNaN(i) || i < INCH_MIN || i >= INCH_LIMIT)
      return "Invalid height input";
    return null;
  };

  return (
    <TextInputModal
      open={open}
      onCancel={onCancel}
      onClose={onClose} // parent's closeHeightModal (reads heightFt/heightInch or heightCmTens/heightCmOnes)
      title="Enter height"
      validate={validate}
      toggleUnits={{
        value: unit,
        onChange: handleUnitChange,
        options: [
          { value: "inch", label: "in" },
          { value: "cm", label: "cm" },
        ],
      }}
    >
      {({ hasError, clearError, submit }) =>
        unit === "cm" ? (
          <NumberField
            value={cm}
            hasError={hasError}
            ariaLabel="Height in centimeters"
            suffix="cm"
            allowDecimal
            maxLength={5} // "273.5" — the 3-digit default would truncate it
            onSubmit={submit}
            onChange={(digits) => {
              setCm(digits);
              writeCmDraft(digits);
              clearError();
            }}
          />
        ) : (
          <FeetInchesField
            feet={feet}
            inches={inches}
            hasError={hasError}
            onSubmit={submit}
            onFeetChange={(digits) => {
              setFeet(digits);
              handlePickerChange("heightFt", {
                heightFt: digits ? `${digits}'` : "",
              });
              clearError();
            }}
            onInchesChange={(digits) => {
              setInches(digits);
              handlePickerChange("heightInch", {
                heightInch: digits !== "" ? `${digits}"` : "",
              });
              clearError();
            }}
          />
        )
      }
    </TextInputModal>
  );
};

export default HeightModal;
